import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useApiData } from '../../contexts/apiService';
import './ComparisonPanel.css';
import { useDataManager } from '../../contexts/dataManager';
import { ChangelogItem } from '../../contexts/tableDefinitions';
import TabToggle from '../../components/common/tab_toggle/tabToggle';
import SQLTable, { RowActionConfig } from '../../components/sql_table/tableMain';
import Dropdown from '../../components/common/dropdown/dropdown';
import {
    SubTableConfig,
    EditResult,
    DeleteResult,
    CreateResult,
    SplitResults,
    DAYS_OPTIONS,
    EDIT_COLUMNS,
    DELETE_COLUMNS,
    CREATE_COLUMNS,
    HISTORICAL_COLUMNS,
    EDIT_ROW_COLOR,
    DELETE_ROW_COLOR,
    CREATE_ROW_COLOR,
    deduplicateChangelog,
    resolveColValue,
    normalizeForCompare,
    getValueFromSubTable,
} from './tableConfigComparison';

interface ComparisonPanelProps {
    tables?: string[];
    monthTrackedTables?: string[];
    subTableConfigs?: SubTableConfig[];
    buttonLabel?: string;
}

const ComparisonPanel = ({
    tables,
    monthTrackedTables = [],
    subTableConfigs = [],
    buttonLabel = 'DB Comparison',
}: ComparisonPanelProps) => {
    const { apiData, handleGetChangeLog } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();

    const [daysBack, setDaysBack] = useState(3);
    const [splitResults, setSplitResults] = useState<SplitResults | null>(null);
    const [comparisonPending, setComparisonPending] = useState(false);
    const [pendingLoads, setPendingLoads] = useState<{ table: string; monthKey?: string }[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState<'edit' | 'delete' | 'create'>('edit');

    const [showHistoricalModal, setShowHistoricalModal] = useState(false);
    const [historicalRows, setHistoricalRows] = useState<Record<string, any>[]>([]);
    const [historicalRID, setHistoricalRID] = useState('');
    const [historicalLoading, setHistoricalLoading] = useState(false);

    const pendingEntriesRef = useRef<ChangelogItem[]>([]);

    const getCurrentValue = useCallback((
        tableName: string,
        recordId: string,
        editedCol: string,
    ): { value: string | null; found: boolean; colInResponse: boolean } => {
        const subConfig = subTableConfigs.find(c => c.tableName === tableName);
        if (subConfig) {
            const result = getValueFromSubTable(apiData, subConfig, recordId, editedCol);
            return { ...result, colInResponse: result.found };
        }

        // No record_id → can't look up the row
        if (!recordId) return { value: null, found: true, colInResponse: false };

        if (tableName === 'main_table') {
            const scheduleData = (apiData as any)?.['schedule_table'] || [];
            const row = scheduleData.find((r: any) => String(r.main_table_id) === String(recordId));
            if (!row) return { value: null, found: false, colInResponse: false };
            const { colValue, colFound } = resolveColValue(row, editedCol);
            return { value: colValue, found: true, colInResponse: colFound };
        }

        const tableData = (apiData as any)?.[tableName] || [];
        const row = tableData.find((r: any) => String(r.id) === String(recordId));
        if (!row) return { value: null, found: false, colInResponse: false };
        const { colValue, colFound } = resolveColValue(row, editedCol);
        return { value: colValue, found: true, colInResponse: colFound };
    }, [apiData, subTableConfigs]);

    const checkRowExists = useCallback((tableName: string, recordId: string, recordRid?: string): boolean => {
        const subConfig = subTableConfigs.find(c => c.tableName === tableName);
        if (subConfig) {
            // First try: sub-row has its own id matching recordId
            if (getValueFromSubTable(apiData, subConfig, recordId, 'id').found) return true;
            // Fallback: changelog may store the parent table's id as record_id.
            // Check if the parent row exists and has any data in the sub-field.
            const parentData = (apiData as any)?.[subConfig.parentTable] || [];
            const parentRow = parentData.find((r: any) => String(r.id) === String(recordId));
            if (parentRow) {
                try {
                    const raw = parentRow[subConfig.parentField];
                    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    return Array.isArray(data) && data.length > 0;
                } catch { return true; }
            }
            return false;
        }
        if (tableName === 'main_table') {
            const scheduleData = (apiData as any)?.['schedule_table'] || [];
            return scheduleData.some((r: any) => String(r.main_table_id) === String(recordId));
        }
        const tableData = (apiData as any)?.[tableName] || [];
        if (recordId) return tableData.some((r: any) => String(r.id) === String(recordId));
        // new_row entries may have empty record_id — fall back to matching by rid
        if (recordRid) return tableData.some((r: any) => String(r.rid) === String(recordRid));
        return false;
    }, [apiData, subTableConfigs]);

    const computeSplitResults = useCallback((allEntries: ChangelogItem[]): SplitResults => {
        const fullChangelog: ChangelogItem[] = (apiData as any)?.changelog || [];

        const deleteEntries = allEntries.filter(e => e.action === 'delete');
        const editEntries = allEntries.filter(e => e.action === 'edit');

        // For create/new_row: if record_id/record_rid are missing, try to extract from row_data
        const createEntries = allEntries
            .filter(e => e.action === 'create' || e.action === 'new_row')
            .map(e => {
                if (e.record_id || e.record_rid) return e;
                try {
                    const parsed = typeof e.row_data === 'string' ? JSON.parse(e.row_data) : e.row_data;
                    if (parsed && (parsed.id || parsed.rid)) {
                        return {
                            ...e,
                            record_id: parsed.id ? String(parsed.id) : e.record_id,
                            record_rid: parsed.rid || e.record_rid,
                        };
                    }
                } catch { /* ignore parse errors */ }
                return e;
            });

        const recordKey = (e: ChangelogItem) => e.record_id || e.record_rid;
        const deleteMap = deduplicateChangelog(deleteEntries, (e, idx) => {
            const rk = recordKey(e);
            return rk ? `${e.table_name}|${rk}` : `${e.table_name}|__unkeyed__${idx}`;
        });
        const createMap = deduplicateChangelog(createEntries, (e, idx) => {
            const rk = recordKey(e);
            return rk ? `${e.table_name}|${rk}` : `${e.table_name}|__unkeyed__${idx}`;
        });
        const editMap = deduplicateChangelog(editEntries, (e) =>
            `${e.table_name}|${e.record_id}|${e.edited_col}`
        );

        // For edit entries where the row no longer exists in the DB → move to delete tab.
        // Prefer a changelog delete entry if one exists; otherwise use the edit entry as a proxy
        // (row may have been deleted without a logged delete action).
        for (const entry of Array.from(editMap.values())) {
            const rk = entry.record_id || entry.record_rid;
            const recordKey = rk ? `${entry.table_name}|${rk}` : null;
            if (recordKey && deleteMap.has(recordKey)) continue;
            // Can't verify row existence without a record_id — leave in edit tab as unverifiable
            if (!entry.record_id) continue;
            if (!checkRowExists(entry.table_name, entry.record_id)) {
                const latestDelete = fullChangelog
                    .filter(e => e.action === 'delete' && e.table_name === entry.table_name && e.record_id === entry.record_id)
                    .sort((a, b) => new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime())[0];
                const key = recordKey ?? `${entry.table_name}|__reclassified__${entry.changed_at}`;
                deleteMap.set(key, latestDelete ?? entry);
            }
        }

        const deletedRecordKeys = new Set(
            Array.from(deleteMap.entries())
                .filter(([k]) => !k.includes('|__unkeyed__'))
                .map(([k]) => k)
        );

        // Process edit entries — skip any whose row is in the delete set
        const editResults: EditResult[] = [];
        for (const entry of Array.from(editMap.values())) {
            const rk = entry.record_id || entry.record_rid;
            const recordKey = rk ? `${entry.table_name}|${rk}` : null;
            if (recordKey && deletedRecordKeys.has(recordKey)) continue;
            const { value: currentValue, found, colInResponse } = getCurrentValue(entry.table_name, entry.record_id, entry.edited_col);
            let status: EditResult['status'];
            if (!found) status = 'not_found';
            else if (!colInResponse) status = 'unverifiable';
            else if (normalizeForCompare(currentValue) === normalizeForCompare(entry.new_value)) status = 'match';
            else status = 'mismatch';
            editResults.push({
                table_name: entry.table_name,
                record_id: entry.record_id,
                record_rid: entry.record_rid,
                edited_col: entry.edited_col,
                changelog_value: entry.new_value,
                current_value: currentValue,
                changed_at: entry.changed_at,
                changed_by: entry.changed_by,
                status,
            });
        }

        // Process delete entries
        const deleteResults: DeleteResult[] = [];
        for (const entry of Array.from(deleteMap.values())) {
            const exists = checkRowExists(entry.table_name, entry.record_id);
            deleteResults.push({
                table_name: entry.table_name,
                record_id: entry.record_id,
                record_rid: entry.record_rid,
                changed_at: entry.changed_at,
                changed_by: entry.changed_by,
                status: exists ? 'not_deleted' : 'deleted',
            });
        }

        // Process create entries
        const createResults: CreateResult[] = [];
        for (const entry of Array.from(createMap.values())) {
            // If we have no identifier at all, we can't verify existence → mark as unknown
            const canVerify = !!(entry.record_id || entry.record_rid);
            let status: CreateResult['status'];
            if (!canVerify) {
                status = 'unknown';
            } else {
                status = checkRowExists(entry.table_name, entry.record_id, entry.record_rid) ? 'created' : 'not_created';
            }
            createResults.push({
                table_name: entry.table_name,
                record_id: entry.record_id,
                record_rid: entry.record_rid,
                changed_at: entry.changed_at,
                changed_by: entry.changed_by,
                status,
            });
        }

        // Sort: problems first, unknown last
        const editOrder = { mismatch: 0, unverifiable: 1, not_found: 2, match: 3 };
        const createOrder = { not_created: 0, created: 1, unknown: 2 };
        editResults.sort((a, b) => editOrder[a.status] - editOrder[b.status]);
        deleteResults.sort((a, b) => (a.status === 'not_deleted' ? 0 : 1) - (b.status === 'not_deleted' ? 0 : 1));
        createResults.sort((a, b) => createOrder[a.status] - createOrder[b.status]);

        return { edit: editResults, delete: deleteResults, create: createResults };
    }, [apiData, getCurrentValue, checkRowExists]);

    useEffect(() => {
        if (!comparisonPending || pendingLoads.length === 0) return;
        const allReady = pendingLoads.every(({ table, monthKey }) => hasTableData(table, monthKey));
        if (!allReady) return;
        setSplitResults(computeSplitResults(pendingEntriesRef.current));
        setComparisonPending(false);
        setPendingLoads([]);
    }, [hasTableData, comparisonPending, pendingLoads, computeSplitResults]);

    const handleRunComparison = () => {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - daysBack);

        const allEntries = (apiData?.changelog || []).filter((entry: ChangelogItem) => {
            const inRange = new Date(entry.changed_at) >= cutoff;
            const inScope = !tables || tables.includes(entry.table_name);
            return inRange && inScope;
        });

        setSplitResults(null);
        setShowModal(true);
        setActiveTab('edit');

        if (allEntries.length === 0) {
            setSplitResults({ edit: [], delete: [], create: [] });
            return;
        }

        pendingEntriesRef.current = allEntries;

        const now = new Date();
        const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const monthKeys = [
            `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
            `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`,
        ];

        const subTableNames = new Set(subTableConfigs.map(c => c.tableName));
        const uniqueTables = Array.from(new Set(
            allEntries
                .map((e: ChangelogItem) => e.table_name)
                .filter((t: string) => !subTableNames.has(t))
        ));

        const tablesToFetch = uniqueTables.filter((t: string) => monthTrackedTables.includes(t));
        const tablesWithoutMonth = uniqueTables.filter((t: string) => !monthTrackedTables.includes(t));

        const neededParents = subTableConfigs
            .filter(c => allEntries.some((e: ChangelogItem) => e.table_name === c.tableName))
            .map(c => c.parentTable)
            .filter(p => monthTrackedTables.includes(p));

        const allMonthTables = Array.from(new Set([...tablesToFetch, ...neededParents]));

        const needed: { table: string; monthKey?: string }[] = [];
        monthKeys.forEach(monthKey => {
            allMonthTables.forEach(table => {
                requestTableData(table, monthKey);
                if (!hasTableData(table, monthKey)) needed.push({ table, monthKey });
            });
        });
        // main_table data is accessed via schedule_table — direct fetch causes 500
        tablesWithoutMonth.filter(t => t !== 'main_table').forEach(table => {
            requestTableData(table);
            if (!hasTableData(table)) needed.push({ table });
        });

        if (needed.length > 0) {
            setPendingLoads(needed);
            setComparisonPending(true);
        } else {
            setSplitResults(computeSplitResults(allEntries));
        }
    };

    const handleWatchHistorical = useCallback(async (rid: string) => {
        setHistoricalRID(rid);
        setHistoricalLoading(true);
        setShowHistoricalModal(true);
        setHistoricalRows([]);
        try {
            const data = await handleGetChangeLog('changelog', rid);
            const sorted = (data ?? [])
                .sort((a: ChangelogItem, b: ChangelogItem) =>
                    new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
                )
                .map((row: ChangelogItem, i: number) => ({ ...row, _id: i }));
            setHistoricalRows(sorted);
        } catch {
            setHistoricalRows([]);
        } finally {
            setHistoricalLoading(false);
        }
    }, [handleGetChangeLog]);

    const deleteRowActions: RowActionConfig[] = useMemo(() => [{
        id: 'see-historical',
        label: 'See Historical',
        variant: 'primary',
        onClick: (rowData) => handleWatchHistorical(rowData.record_rid || rowData.record_id),
    }], [handleWatchHistorical]);

    // Transform results into SQLTable-compatible row objects
    const editRows = useMemo(() => (splitResults?.edit ?? []).map((r, i) => ({
        _id: i,
        status_display: r.status === 'match' ? '✓ Match' : r.status === 'mismatch' ? '✗ Mismatch' : r.status === 'unverifiable' ? '? Unverifiable' : '? Not Found',
        _status: r.status,
        table_name: r.table_name,
        record_id: r.record_id,
        record_rid: r.record_rid,
        edited_col: r.edited_col,
        changelog_value: r.changelog_value,
        current_value: r.current_value ?? '—',
        changed_by: r.changed_by,
        changed_at: r.changed_at,
    })), [splitResults]);

    const deleteRows = useMemo(() => (splitResults?.delete ?? []).map((r, i) => ({
        _id: i,
        status_display: r.status === 'deleted' ? '✓ Deleted' : '✗ Not Deleted',
        _status: r.status,
        table_name: r.table_name,
        record_id: r.record_id,
        record_rid: r.record_rid,
        changed_by: r.changed_by,
        changed_at: r.changed_at,
    })), [splitResults]);

    const createRows = useMemo(() => (splitResults?.create ?? []).map((r, i) => ({
        _id: i,
        status_display: r.status === 'created' ? '✓ Created' : r.status === 'not_created' ? '✗ Not Created' : '? Unverifiable',
        _status: r.status,
        table_name: r.table_name,
        record_id: r.record_id,
        record_rid: r.record_rid,
        changed_by: r.changed_by,
        changed_at: r.changed_at,
    })), [splitResults]);


    const editMatches = splitResults?.edit.filter(r => r.status === 'match').length ?? 0;
    const editMismatches = splitResults?.edit.filter(r => r.status === 'mismatch').length ?? 0;
    const editUnverifiable = splitResults?.edit.filter(r => r.status === 'unverifiable').length ?? 0;
    const deleteProblems = splitResults?.delete.filter(r => r.status === 'not_deleted').length ?? 0;
    const createProblems = splitResults?.create.filter(r => r.status === 'not_created').length ?? 0;
    const createUnknown = splitResults?.create.filter(r => r.status === 'unknown').length ?? 0;

    return (
        <>
            <button onClick={handleRunComparison} className="comparison-btn">
                {buttonLabel}
            </button>

            {showModal && (
                <div className="comparison-overlay">
                    <div className="comparison-modal">
                        <div className="comparison-modal__header">
                            <div className="comparison-modal__header-left">
                                <h3 className="comparison-modal__title">DB Comparison</h3>
                                <Dropdown
                                    options={DAYS_OPTIONS}
                                    value={String(daysBack)}
                                    onChange={(val) => setDaysBack(Number(val))}
                                    className="comparison-days-dropdown"
                                />
                                <button onClick={handleRunComparison} className="comparison-btn comparison-btn--rerun">
                                    Run
                                </button>
                            </div>
                            <button onClick={() => setShowModal(false)} className="comparison-modal__close-btn">
                                Close
                            </button>
                        </div>

                        {splitResults === null ? (
                            <p className="comparison-loading">Loading comparison data...</p>
                        ) : (
                            <div className="comparison-modal__body">
                                <TabToggle
                                    activeTab={activeTab}
                                    tabOptions={[
                                        {
                                            label: `Edit (${splitResults.edit.length})  ·  ${editMatches} match  ·  ${editMismatches} mismatch${editUnverifiable > 0 ? `  ·  ${editUnverifiable} unverifiable` : ''}`,
                                            value: 'edit',
                                        },
                                        {
                                            label: `Delete (${splitResults.delete.length})${deleteProblems > 0 ? `  ·  ${deleteProblems} issues` : ''}`,
                                            value: 'delete',
                                        },
                                        {
                                            label: `Create (${splitResults.create.length})${createProblems > 0 ? `  ·  ${createProblems} issues` : ''}${createUnknown > 0 ? `  ·  ${createUnknown} unverifiable` : ''}`,
                                            value: 'create',
                                        },
                                    ]}
                                    onChange={(val) => setActiveTab(val as 'edit' | 'delete' | 'create')}
                                />

                                <div className="comparison-table-area">
                                    {activeTab === 'edit' && (
                                        splitResults.edit.length === 0
                                            ? <p className="comparison-empty">No edit entries in the selected range.</p>
                                            : <SQLTable
                                                tableName="db-comparison-edit"
                                                tableData={editRows}
                                                idColumn="_id"
                                                columnConfigs={EDIT_COLUMNS}
                                                showHeader={true}
                                                tableTools={true}
                                                enableFilters={true}
                                                downloadable={true}
                                                refreshable={false}
                                                selectableRows={false}
                                                deletableRows={false}
                                                userRoleLevel={1}
                                                showTable={true}
                                                pageSize={0}
                                                rowColor={EDIT_ROW_COLOR}
                                            />
                                    )}

                                    {activeTab === 'delete' && (
                                        splitResults.delete.length === 0
                                            ? <p className="comparison-empty">No delete entries in the selected range.</p>
                                            : <SQLTable
                                                tableName="db-comparison-delete"
                                                tableData={deleteRows}
                                                idColumn="_id"
                                                columnConfigs={DELETE_COLUMNS}
                                                showHeader={true}
                                                tableTools={true}
                                                enableFilters={true}
                                                downloadable={true}
                                                refreshable={false}
                                                selectableRows={false}
                                                deletableRows={false}
                                                userRoleLevel={1}
                                                showTable={true}
                                                pageSize={0}
                                                rowColor={DELETE_ROW_COLOR}
                                                rowActions={deleteRowActions}
                                                rowActionsPosition="end"
                                                rowActionsWidth={130}
                                            />
                                    )}

                                    {activeTab === 'create' && (
                                        splitResults.create.length === 0
                                            ? <p className="comparison-empty">No create entries in the selected range.</p>
                                            : <SQLTable
                                                tableName="db-comparison-create"
                                                tableData={createRows}
                                                idColumn="_id"
                                                columnConfigs={CREATE_COLUMNS}
                                                showHeader={true}
                                                tableTools={true}
                                                enableFilters={true}
                                                downloadable={true}
                                                refreshable={false}
                                                selectableRows={false}
                                                deletableRows={false}
                                                userRoleLevel={1}
                                                showTable={true}
                                                pageSize={0}
                                                rowColor={CREATE_ROW_COLOR}
                                            />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Historical modal */}
            {showHistoricalModal && (
                <div className="comparison-overlay comparison-overlay--nested">
                    <div className="comparison-modal comparison-modal--historical">
                        <div className="comparison-modal__header">
                            <h3 className="comparison-modal__title">History — {historicalRID}</h3>
                            <button
                                onClick={() => setShowHistoricalModal(false)}
                                className="comparison-modal__close-btn"
                            >
                                Close
                            </button>
                        </div>
                        {historicalLoading ? (
                            <p className="comparison-loading">Loading history...</p>
                        ) : historicalRows.length > 0 ? (
                            <div className="comparison-table-area">
                                <SQLTable
                                    tableName="db-comparison-historical"
                                    tableData={historicalRows}
                                    idColumn="_id"
                                    columnConfigs={HISTORICAL_COLUMNS}
                                    showHeader={true}
                                    tableTools={true}
                                    enableFilters={true}
                                    downloadable={false}
                                    refreshable={false}
                                    selectableRows={false}
                                    deletableRows={false}
                                    userRoleLevel={1}
                                    showTable={true}
                                    pageSize={50}
                                />
                            </div>
                        ) : (
                            <p>No history found for this record.</p>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ComparisonPanel;
